<script lang="ts" setup generic="T">
import { type Reactive } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

type ListItem = T & { id: string | number };

const props = withDefaults(
  defineProps<{
    modelValue: T | null;
    options: ListItem[];
    placeholder?: string;
    error?: boolean;
  }>(),
  {
    placeholder: '',
    error: false,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: Reactive<ListItem>): void;
}>();

const isSelectOpen = ref<boolean>(false);

const toggleSelect = function toggleSelect(state: boolean) {
  isSelectOpen.value = state;
};

const onItemClick = function onItemClickHandler(item: Reactive<ListItem>) {
  emit('update:modelValue', item);
  toggleSelect(false);
};
</script>

<template>
  <div
    v-on-click-outside="() => toggleSelect(false)"
    class="simple-select"
    :class="{
      'simple-select--active': isSelectOpen,
      'simple-select--error': props.error,
    }"
  >
    <div
      class="simple-select__header"
      @click="toggleSelect(!isSelectOpen)"
    >
      <div class="simple-select__header-content">
        <slot
          v-if="props.modelValue"
          name="item"
          :data="props.modelValue"
        ></slot>

        <span
          v-else
          class="text-body-s"
          >{{ props.placeholder }}</span
        >
      </div>

      <svg
        class="simple-select__arrow"
        width="24"
        height="24"
      >
        <use
          xlink:href="@/assets/images/svg/sprite-base.svg#chevron-down-24"
        ></use>
      </svg>
    </div>

    <div class="simple-select__wrap">
      <ul class="simple-select__list scroll">
        <li
          v-for="item in props.options"
          :key="item.id"
          class="simple-select__option"
          :data-testid="item.id"
          @click="onItemClick(item as Reactive<ListItem>)"
        >
          <slot
            name="item"
            :data="item"
          ></slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.simple-select {
  $this: 'simple-select';

  position: relative;

  &--active {
    .#{$this}__header {
      background-color: $color-white;
      border-color: $color-new-black-600;
    }

    .#{$this}__arrow {
      transform: rotate(180deg);
    }

    .#{$this}__wrap {
      display: block;
    }
  }

  &--error {
    .#{$this}__header {
      background-color: $color-red-100;
      border-color: $color-red-500;

      &:hover {
        background-color: $color-red-100;
        border-color: $color-red-500;
      }

      &:focus {
        background-color: $color-white;
        border-color: $color-red-500;
      }
    }
  }

  &__header {
    cursor: pointer;
    display: flex;
    align-items: center;
    column-gap: 8px;
    height: 56px;
    border-radius: $brd-12;
    padding-inline: 16px;
    border: 1px solid $color-dark-blue-100;
    background-color: $color-dark-blue-100;
    transition: all $anim-primary;

    &:hover {
      @include media('md') {
        border-color: $color-new-black-600;
      }
    }
  }

  &__header-content {
    flex-grow: 1;
    overflow: hidden;
  }

  &__arrow {
    flex-shrink: 0;
    color: $color-new-black-600;
    transition: transform $anim-primary;
  }

  &__wrap {
    $shadow-card-active: 0px 12px 40px -8px rgb(0 16 80 / 15%);

    box-shadow: $shadow-card-active;
    border-radius: $brd-12;
    border: 1px solid $color-dark-blue-200;
    background-color: $color-white;
    overflow: hidden;
    position: absolute;
    z-index: 1;
    top: 60px;
    left: 0;
    min-width: 100%;
    display: none;
  }

  &__list {
    @include reset-list;

    overflow: auto;
  }

  &__option {
    cursor: pointer;
    display: flex;
    align-items: center;
    padding-inline: 16px;
    height: 48px;
    transition: background-color $anim-primary;

    &:hover {
      @include media('md') {
        background-color: $color-dark-blue-200;
      }
    }
  }
}
</style>
